import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Plus } from "lucide-react";
import { MockCard } from "../components/MockCard";
import { CardCarousel } from "../components/CardCarousel";
import { useAuth } from "../lib/auth";
import { GET_DEPOSIT_HISTORY } from "../graphql/crypto";
import { GET_DASHBOARD_DATA } from "../graphql/dashboard";
import { useBalance } from "../hooks/use-balance";
import { enableBackButton } from "../lib/telegram";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";
import { Skeleton } from "../components/SkeletonLoader";
import { parseMoney } from "../lib/money";

const cryptoIcons: Record<string, string> = {
  "BTC-USD": "BTC",
  "ETH-USD": "ETH",
  "SOL-USD": "SOL",
  "TRX-USD": "TRX",
  "USDC-USD": "USDC",
  "USDT-USD": "USDT",
};

export const Wallet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userBalance, companyName, loading: balanceLoading } = useBalance();

  const { data: dashboardData, loading: cardsLoading } = useQuery(GET_DASHBOARD_DATA, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const { data: depositData, loading: depositsLoading } = useQuery(
    GET_DEPOSIT_HISTORY,
    {
      variables: { userId: user?.id, limit: 20 },
      skip: !user?.id,
    }
  );

  useEffect(() => {
    return enableBackButton(() => navigate("/"));
  }, [navigate]);

  const physicalCards = (dashboardData?.physical_cards || []).map((c: any) => ({
    id: c.id,
    card_name: c.card_name || "Physical Card",
    status: c.status,
    type: "physical" as const,
    lastFour: c.card_number_last_four,
    balance: parseMoney(c.available_balance),
    expiry: c.expiry_month && c.expiry_year ? `${String(c.expiry_month).padStart(2, "0")}/${String(c.expiry_year).slice(-2)}` : undefined,
  }));

  const virtualCards = (dashboardData?.virtual_cards || []).map((c: any) => ({
    id: c.id,
    card_name: c.card_name,
    status: c.status,
    type: "virtual" as const,
    lastFour: c.card_number_last_four,
    balance: parseMoney(c.current_balance),
    limit: parseMoney(c.spending_limit),
    expiry: c.expiry_date ? `${c.expiry_date.slice(5, 7)}/${c.expiry_date.slice(2, 4)}` : undefined,
  }));

  const allCards = [...physicalCards, ...virtualCards];
  const hasCards = allCards.length > 0;
  const deposits = depositData?.crypto_deposits || [];

  return (
    <PageContainer>
      <GradientHeader size="md">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted mb-2">
          {companyName || "Wallet"}
        </p>
        {balanceLoading ? (
          <div className="skeleton h-10 w-48" />
        ) : (
          <>
            <p className="money text-[40px] leading-none font-semibold text-ink">
              ${userBalance.toFixed(2)}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted mt-2">
              My balance
            </p>
          </>
        )}
      </GradientHeader>

      {/* Deposit Crypto Button */}
      <div className="px-4 mt-4 mb-4">
        <button
          onClick={() => navigate("/deposit")}
          className="flex items-center justify-center gap-2 w-full py-3.5 bg-ink text-ground rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
        >
          <Plus size={16} />
          Deposit crypto
        </button>
      </div>

      {/* My Cards */}
      <div className="px-4 mb-4">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted mb-2.5">
          My cards
        </p>
        {cardsLoading ? (
          <Skeleton className="h-44 w-full" />
        ) : hasCards ? (
          <CardCarousel cards={allCards} />
        ) : (
          <div className="flex flex-col items-center text-center py-6 bg-surface rounded-xl border border-hairline">
            <MockCard size="sm" className="w-full mb-4 px-4" />
            <p className="font-medium text-ink">No cards yet</p>
            <p className="text-xs text-muted mt-1.5 max-w-[220px]">
              Deposit at least $25 in crypto to mint your card and start spending
            </p>
          </div>
        )}
      </div>

      {/* Deposit History */}
      <div className="px-4">
        <SectionCard title="Deposit history">
          {depositsLoading ? (
            <div className="space-y-3 pt-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : deposits.length === 0 ? (
            <div className="flex flex-col items-center text-center py-6">
              <p className="font-medium text-ink">No deposits yet</p>
              <p className="text-xs text-muted mt-1.5 max-w-[220px]">
                Your deposit history will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-hairline">
              {deposits.map((d: any) => (
                <div key={d.id} className="flex items-center gap-3 py-3.5">
                  <div className="w-9 h-9 rounded-full bg-ground border border-hairline flex items-center justify-center flex-shrink-0">
                    <span className="font-mono text-[10px] font-semibold text-ink">
                      {cryptoIcons[d.crypto_type] || "?"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-ink">
                      {cryptoIcons[d.crypto_type] || d.crypto_type}
                    </p>
                    <p className="font-mono text-[11px] text-muted uppercase tracking-[0.04em]">
                      {new Date(d.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      {" · "}
                      {d.status}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`money font-semibold text-sm ${
                        d.status === "confirmed" ? "text-success" : "text-muted"
                      }`}
                    >
                      {d.status === "confirmed" ? "+" : ""}$
                      {(Number(d.usd_amount) || 0).toFixed(2)}
                    </p>
                    <p className="money text-[10px] text-muted">
                      {Number(d.crypto_amount).toFixed(6)}{" "}
                      {cryptoIcons[d.crypto_type] || ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </PageContainer>
  );
};
